import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { ReturnsAndClaimsPermissions } from "@redotech/redo-model/permissions";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { Navigate } from "react-router-dom";
import { MerchantRouteNavigate } from "../redo-suite/merchant-routes";

const returnBreadcrumb = breadcrumbSlot();
const returnFolderBreadcrumb = breadcrumbSlot();
const returnViewBreadcrumb = breadcrumbSlot();

const claimFolderBreadcrumb = breadcrumbSlot();
const claimViewBreadcrumb = breadcrumbSlot();

const warrantyFolderBreadcrumb = breadcrumbSlot();
const warrantyViewBreadcrumb = breadcrumbSlot();

const recycleFolderBreadcrumb = breadcrumbSlot();
const recycleViewBreadcrumb = breadcrumbSlot();

const carrierClaimDetailBreadcrumb = breadcrumbSlot();

// The return detail page renders its own header inside the left column, so it
// opts out of the shared page header. `Page` reads this via `useMatches`.
const returnDetailHandle = { breadcrumb: returnBreadcrumb, hideHeader: true };

export const returnRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Returns" },
    children: [
      { index: true, element: <Navigate replace to="all-returns" /> },
      {
        path: "all-returns",
        handle: { breadcrumb: returnViewBreadcrumb },
        children: [
          {
            index: true,
            async lazy() {
              const { ReturnWrapper } = await import("./returns");
              return {
                element: (
                  <ReturnWrapper
                    folderBreadcrumb={returnFolderBreadcrumb}
                    viewBreadcrumb={returnViewBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":returnId",
            handle: returnDetailHandle,
            children: [
              {
                index: true,
                async lazy() {
                  const { WrappedReturnPage } = await import("./return");
                  return {
                    element: (
                      <WrappedReturnPage
                        breadcrumb={returnBreadcrumb}
                        returnType="return"
                        viewBreadcrumb={returnViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          breadcrumb: returnFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: returnViewBreadcrumb },
            async lazy() {
              const { ReturnWrapper } = await import("./returns");
              return {
                element: (
                  <ReturnWrapper
                    folderBreadcrumb={returnFolderBreadcrumb}
                    viewBreadcrumb={returnViewBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":returnId",
            handle: returnDetailHandle,
            children: [
              {
                index: true,
                async lazy() {
                  const { WrappedReturnPage } = await import("./return");
                  return {
                    element: (
                      <WrappedReturnPage
                        breadcrumb={returnBreadcrumb}
                        returnType="return"
                        viewBreadcrumb={returnViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-return/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } = await import(
                "./create-return"
              );
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.RETURN}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "instant-exchange",
        children: [
          {
            index: true,
            handle: { breadcrumb: "Instant exchange recovery" },
            async lazy() {
              const { InstantExchangeRecovery } = await import(
                "./instant-exchange-recovery"
              );
              return { element: <InstantExchangeRecovery /> };
            },
          },
        ],
      },
      {
        path: "gift-receipt-requests",
        handle: { breadcrumb: "Gift Receipt Requests" },
        async lazy() {
          const { GiftReceiptRequests } = await import(
            "../gift-receipt-requests/gift-receipt-requests"
          );
          return { element: <GiftReceiptRequests /> };
        },
      },
      {
        path: "inmar-blind-returns",
        handle: { breadcrumb: "Inmar Blind Returns" },
        async lazy() {
          const { InmarBlindReturns } = await import(
            "../inmar-blind-returns/inmar-blind-returns"
          );
          return { element: <InmarBlindReturns /> };
        },
      },
    ],
  },
];

export const fraudRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Fraud" },
    children: [
      {
        index: true,
        async lazy() {
          const { FraudDashboardPage } = await import(
            "./fraud/fraud-dashboard"
          );
          const { ZipCodeFraudFeatureFlagGate } = await import(
            "./fraud/zip-code-fraud-feature-flag-gate"
          );
          return {
            element: (
              <ZipCodeFraudFeatureFlagGate>
                <FraudDashboardPage />
              </ZipCodeFraudFeatureFlagGate>
            ),
          };
        },
      },
    ],
  },
];

export const carrierClaimsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Carrier claims",
      permission: [ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM],
    },
    children: [
      {
        index: true,
        async lazy() {
          const { MerchantShippingClaimsPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/shipping-claims/merchant-shipping-claims-page"
          );
          return { element: <MerchantShippingClaimsPage /> };
        },
      },
      {
        path: ":claim_id",
        handle: { breadcrumb: carrierClaimDetailBreadcrumb },
        async lazy() {
          const { MerchantShippingClaimDetailPage } = await import(
            "@redotech/redo-merchant-fulfillment-app/shipping-claims/merchant-shipping-claim-detail-page"
          );
          return {
            element: (
              <MerchantShippingClaimDetailPage
                breadcrumb={carrierClaimDetailBreadcrumb}
              />
            ),
          };
        },
      },
    ],
  },
];

const createClaimIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="claim"
              viewBreadcrumb={claimViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

export const claimRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Claims" },
    children: [
      {
        index: true,
        element: (
          <MerchantRouteNavigate
            routeTo={(routes) => routes.claimsDefaultRoute}
          />
        ),
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-claim/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } = await import(
                "./create-return"
              );
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.CLAIM}
                  />
                ),
              };
            },
          },
        ],
      },
      // Needs a separate route because the navigation item is a child of the index route
      {
        path: "all",
        handle: {
          breadcrumb: claimViewBreadcrumb,
          permission: [
            ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { ClaimWrapper } = await import("./claims");
              return {
                element: (
                  <ClaimWrapper
                    folderBreadcrumb={claimFolderBreadcrumb}
                    viewBreadcrumb={claimViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createClaimIdRoute(),
        ],
      },
      { path: "carrier-claims", children: carrierClaimsRoutes },
      {
        handle: {
          breadcrumb: claimFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: claimViewBreadcrumb },
            async lazy() {
              const { ClaimWrapper } = await import("./claims");
              return {
                element: (
                  <ClaimWrapper
                    folderBreadcrumb={claimFolderBreadcrumb}
                    viewBreadcrumb={claimViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createClaimIdRoute(),
        ],
      },
    ],
  },
];

const createWarrantyIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="warranty"
              viewBreadcrumb={warrantyViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

export const warrantyRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Warranties" },
    children: [
      {
        index: true,
        async lazy() {
          const { WarrantyWrapper } = await import("./warranties");
          return {
            element: (
              <WarrantyWrapper
                folderBreadcrumb={warrantyFolderBreadcrumb}
                viewBreadcrumb={warrantyViewBreadcrumb}
              />
            ),
          };
        },
      },
      {
        path: "about",
        async lazy() {
          const { AboutWarranties } = await import("./about-warranties");
          return { element: <AboutWarranties /> };
        },
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-warranty-claim",
        children: [
          {
            path: "registration/:registrationId",
            async lazy() {
              const { CreateReturnPage: CreateReturn } = await import(
                "./create-return"
              );
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.WARRANTY}
                  />
                ),
              };
            },
          },
          {
            path: ":orderId",
            async lazy() {
              const { CreateReturnPage: CreateReturn } = await import(
                "./create-return"
              );
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.WARRANTY}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "all-warranties",
        handle: {
          breadcrumb: warrantyViewBreadcrumb,
          permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WarrantyWrapper } = await import("./warranties");
              return {
                element: (
                  <WarrantyWrapper
                    folderBreadcrumb={warrantyFolderBreadcrumb}
                    viewBreadcrumb={warrantyViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createWarrantyIdRoute(),
        ],
      },
      {
        handle: {
          breadcrumb: warrantyFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: warrantyViewBreadcrumb },
            async lazy() {
              const { WarrantyWrapper } = await import("./warranties");
              return {
                element: (
                  <WarrantyWrapper
                    folderBreadcrumb={warrantyFolderBreadcrumb}
                    viewBreadcrumb={warrantyViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createWarrantyIdRoute(),
        ],
      },
    ],
  },
];

const createRecycleIdRoute = (): PermissionedRouteObject => ({
  handle: returnDetailHandle,
  path: ":returnId",
  children: [
    {
      index: true,
      async lazy() {
        const { WrappedReturnPage } = await import("./return");
        return {
          element: (
            <WrappedReturnPage
              breadcrumb={returnBreadcrumb}
              returnType="recycle"
              viewBreadcrumb={recycleViewBreadcrumb}
            />
          ),
        };
      },
    },
  ],
});

const withdrawalRecordBreadcrumb = breadcrumbSlot();

// The withdrawal detail component may delegate to the return detail page when
// the withdrawal is backed by a return. Keep the shared header hidden so that
// branch does not render a duplicate header; the order-only branch renders its
// own header.
const createWithdrawalRecordIdRoute = (): PermissionedRouteObject => ({
  handle: { breadcrumb: withdrawalRecordBreadcrumb, hideHeader: true },
  path: ":withdrawalId",
  children: [
    {
      index: true,
      async lazy() {
        const { WithdrawalDetailsPage } = await import(
          "./withdrawals/withdrawal-details-page"
        );
        return {
          element: (
            <WithdrawalDetailsPage breadcrumb={withdrawalRecordBreadcrumb} />
          ),
        };
      },
    },
  ],
});

export const recycleRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Recycle" },
    children: [
      {
        index: true,
        async lazy() {
          const { RecycleWrapper } = await import("./recycle");
          return {
            element: (
              <RecycleWrapper
                folderBreadcrumb={recycleFolderBreadcrumb}
                viewBreadcrumb={recycleViewBreadcrumb}
              />
            ),
          };
        },
      },
      {
        handle: { breadcrumb: returnBreadcrumb },
        path: "create-recycle-request/:orderId",
        children: [
          {
            index: true,
            async lazy() {
              const { CreateReturnPage: CreateReturn } = await import(
                "./create-return"
              );
              return {
                element: (
                  <CreateReturn
                    breadcrumb={returnBreadcrumb}
                    type={ReturnTypeEnum.RECYCLE}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "all-recycle",
        handle: {
          breadcrumb: recycleViewBreadcrumb,
          permission: [ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { RecycleWrapper } = await import("./recycle");
              return {
                element: (
                  <RecycleWrapper
                    folderBreadcrumb={recycleFolderBreadcrumb}
                    viewBreadcrumb={recycleViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createRecycleIdRoute(),
        ],
      },
      {
        handle: {
          breadcrumb: recycleFolderBreadcrumb,
          breadcrumbNavigate: false,
        },
        path: ":viewName",
        children: [
          {
            index: true,
            handle: { breadcrumb: recycleViewBreadcrumb },
            async lazy() {
              const { RecycleWrapper } = await import("./recycle");
              return {
                element: (
                  <RecycleWrapper
                    folderBreadcrumb={recycleFolderBreadcrumb}
                    viewBreadcrumb={recycleViewBreadcrumb}
                  />
                ),
              };
            },
          },
          createRecycleIdRoute(),
        ],
      },
    ],
  },
];

export const withdrawalRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Withdrawals" },
    children: [
      { index: true, element: <Navigate replace to="all-withdrawals" /> },
      {
        path: "all-withdrawals",
        handle: {
          breadcrumb: "All withdrawals",
          permission: [ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS],
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WithdrawalsView } = await import(
                "./withdrawals/withdrawals-view"
              );
              const { WithdrawalListView } = await import(
                "@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals"
              );
              return {
                element: <WithdrawalsView view={WithdrawalListView.ALL} />,
              };
            },
          },
          createWithdrawalRecordIdRoute(),
        ],
      },
      {
        path: "needs-action",
        handle: {
          breadcrumb: "Needs action",
          permission: ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
        },
        children: [
          {
            index: true,
            async lazy() {
              const { WithdrawalsView } = await import(
                "./withdrawals/withdrawals-view"
              );
              const { WithdrawalListView } = await import(
                "@redotech/merchant-sdk/rpc/schema/withdrawals/list-withdrawals"
              );
              return {
                element: (
                  <WithdrawalsView view={WithdrawalListView.NEEDS_ACTION} />
                ),
              };
            },
          },
          createWithdrawalRecordIdRoute(),
        ],
      },
    ],
  },
];
